import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL parameters supported for pre-filling the registration form.
 * Matches Vue's RegisterForm.vue behavior.
 *
 * Example URL: /register?username=donjoe&full_name=Don%20Joe&email=bebicat@stanwith.me&phone_number=+16479119111
 */
export interface UrlPrefillValues {
    username?: string;
    fullName?: string;
    email?: string;
    phoneNumber?: string;
    password?: string;
}

/**
 * Hook to extract pre-fill values from URL parameters.
 * Useful for marketing campaigns and integrations.
 *
 * Supported URL parameters:
 * - username: Pre-fill username field
 * - full_name: Pre-fill full name field
 * - email: Pre-fill email field
 * - phone_number: Pre-fill phone number field
 * - password: Pre-fill password field (use with caution)
 */
export function useUrlPrefill(): UrlPrefillValues {
    const [searchParams] = useSearchParams();

    return useMemo(() => {
        const getValue = (key: string): string | undefined => {
            const value = searchParams.get(key);
            // Return undefined if empty string or null
            return value !== '' && value !== null ? value : undefined;
        };

        return {
            username: getValue('username'),
            fullName: getValue('full_name'),
            email: getValue('email'),
            phoneNumber: getValue('phone_number'),
            password: getValue('password'),
        };
    }, [searchParams]);
}
